<template>
  <span v-if="rating && canViewRating" class="lead-communication-rating">
    <button
      type="button"
      class="lead-communication-rating__badge"
      :class="`lead-communication-rating__badge--${tone}`"
      :aria-expanded="String(open)"
      :aria-controls="panelId"
      :aria-label="`AI rating of the first reply: ${scoreLabel} out of 10. ${open ? 'Hide' : 'Show'} the feedback`"
      @click.stop="open = !open"
    >
      AI {{ scoreLabel }}/10
    </button>
    <span
      v-if="open"
      :id="panelId"
      class="lead-communication-rating__panel"
      role="note"
    >
      <strong v-if="rating.strength">{{ rating.strength }}</strong>
      <span v-else>{{ rating.explanation }}</span>
      <template v-if="rating.issues && rating.issues.length">
        <strong class="lead-communication-rating__heading">What to improve</strong>
        <ol class="lead-communication-rating__items">
          <li v-for="(issue, index) in rating.issues" :key="index">
            <p>{{ issue }}</p>
            <span>Recommendation</span>
            <p>{{ rating.improvements[index] }}</p>
          </li>
        </ol>
      </template>
    </span>
  </span>
</template>

<script>
export default {
  name: 'LeadCommunicationRating',
  props: {
    rating: {
      type: Object,
      default: null
    },
    communicationId: {
      type: [Number, String],
      required: true
    }
  },
  data: () => ({ open: false }),
  computed: {
    canViewRating() {
      const roles = ['general_manager', 'sales_supervisor', 'marketing_supervisor']
      return (this.$store.state.userGroups || []).some(group => roles.includes(group.code))
    },
    panelId() {
      return `first-reply-rating-${this.communicationId}`
    },
    scoreLabel() {
      return Number(this.rating.score).toFixed(1)
    },
    tone() {
      if (this.rating.score >= 8) return 'green'
      if (this.rating.score >= 5) return 'amber'
      return 'red'
    }
  }
}
</script>

<style lang="scss" scoped>
.lead-communication-rating {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: $ui-space-4;
  max-width: 100%;

  &__badge {
    padding: 2px 6px;
    border: 1px solid transparent;
    border-radius: 6px;
    color: #fff;
    font-family: $ui-font-heading;
    font-size: 10px;
    font-weight: 600;
    line-height: 14px;
    cursor: pointer;

    &:focus-visible {
      outline: 2px solid $ui-blue;
      outline-offset: 2px;
    }

    &--green {
      border-color: $ui-green;
      background: $ui-green;
    }

    &--amber {
      border-color: $ui-amber;
      background: $ui-amber;
    }

    &--red {
      border-color: $ui-red;
      background: $ui-red;
    }
  }

  &__panel {
    display: flex;
    flex-basis: 100%;
    padding: $ui-space-8;
    border: 1px solid $ui-line;
    border-radius: $ui-control-radius;
    background: $ui-surface;
    color: $ui-text;
    font-size: 11px;
    line-height: 16px;
    text-align: left;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    gap: $ui-space-8;
    flex-direction: column;
  }

  &__heading {
    margin-top: $ui-space-4;
  }

  &__items {
    display: grid;
    gap: $ui-space-8;
    margin: 0;
    padding: 0;
    list-style: none;

    li + li {
      padding-top: $ui-space-8;
      border-top: 1px solid $ui-line;
    }

    p {
      margin: 0;
    }

    span {
      display: block;
      margin-top: $ui-space-4;
      color: $ui-muted;
      font-weight: 600;
    }
  }
}
</style>
